import * as React from "react";
import { useHistory } from "react-router";

import { toAssistantPermissions, URLS, useAccountSettings, useSocialCoachSession } from "@/utils";

import { PageWrapper } from "@/components/PageWrapper";
import { useMemo } from "react";
import { CoachComponents, SegmentationDetail, WebComponents } from "@/interfaces";
import { InternalGrid } from "@/styling/baseStyle";
import { Formik } from "formik";
import { validationSchema } from "./validations";
import { AssistantForm } from "@/components/AssistantForm";
import { AssistantServices } from "@/services";

const CreateAssistantFC: React.FC = () => {
  const history = useHistory();
  const { coachId, session } = useSocialCoachSession();
  const formSubmitRef = React.useRef<HTMLFormElement>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const goToAssistantsList = () => {
    setLoading(false);
    history.push(URLS.coach.assistants);
  };

  const actions = useMemo(() => {
    return [{ text: "Save", onClick: () => formSubmitRef?.current?.handleSubmit(), loading: loading }];
  }, [loading]);

  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.ASSISTANTS);

  const initialPermissions = useMemo(() => {
    return Object.values(CoachComponents).reduce((a, p) => {
      return { ...a, [p.id]: "WRITE" };
    }, {});
  }, []);

  return (
    <PageWrapper
      title="Create a New Assistant"
      breadcrumbs={[{ name: "Users", link: URLS.coach.assistants }]}
      actions={actions}
    >
      <InternalGrid>
        <Formik
          validateOnBlur={false}
          validateOnChange={true}
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            branches: [],
            states: [],
            regions: [],
            permissionsMap: initialPermissions,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, setErrors, validateForm }) => {
            const { firstName, lastName, email, password, branches, regions } = values;
            try {
              const errors = await validateForm({
                firstName,
                lastName,
                email,
                password,
              });

              console.log(errors);

              if (errors && Object.values(errors).length > 0) {
                setErrors(errors);
              } else {
                setLoading(true);
                const branchesToSave: SegmentationDetail[] = branches.map(branchId => ({
                  segmentationDetailId: Number(branchId),
                  coachId: coachId!,
                  type: "BRANCH",
                }));

                const regionsToSave: SegmentationDetail[] = regions.map(branchId => ({
                  segmentationDetailId: Number(branchId),
                  coachId: coachId!,
                  type: "REGION",
                }));
                const permissionsArray = toAssistantPermissions(values.permissionsMap);
                await AssistantServices.createAssistant(coachId!, {
                  account: {
                    password: password!,
                    firstName: firstName!,
                    lastName: lastName!,
                    email: email!,
                    phone: session?.phone,
                    permissions: permissionsArray,
                  },
                  segmentation: branchesToSave.concat(regionsToSave),
                });
                alert("Assistant created successfully");
                goToAssistantsList();
              }
            } catch (e) {
              console.error(e);
              setErrors({
                email: String(e) || "",
                password: undefined,
                firstName: undefined,
                lastName: undefined,
              });
              setLoading(false);
            }
            setSubmitting(false);
          }}
        >
          {({ values, errors, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
            <AssistantForm
              messages={{ error: Object.values(errors) as string[] }}
              coachId={coachId!}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              formSubmitRef={formSubmitRef}
              handleSetFieldValue={setFieldValue}
              isSubmitting={isSubmitting}
              withWritePermission={withWritePermission}
              values={values}
            />
          )}
        </Formik>
      </InternalGrid>
    </PageWrapper>
  );
};

export const CreateAssistant = CreateAssistantFC;
